<template>
  <div class="container-custom moo-sheet-page moo-transactions-page" :style="styleObject">
    <div class="content-custom">
      <div class="moo-sheet-shell">
        <MooSheetHead
          icon="receipt_long"
          :title="$t('profile.menuTransactions')"
          @close="goBack()"
        />

        <div v-if="isFirstLoadDone && isLogined">
          <div class="tx-tabs">
            <button
              v-for="tab in tabs"
              :key="tab.type"
              type="button"
              class="tx-tab"
              :class="{ 'tx-tab--active': typeSelected === tab.type }"
              @click="changeType(tab.type)"
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Wallet / Point: available balance hero -->
          <div class="sheet-balance tx-balance" v-if="isBalanceTab">
            <div class="sheet-balance__info">
              <div class="sheet-balance__label">{{ $t('transactions.walletBalance') }}</div>
              <div class="sheet-balance__amount">
                {{ typeSelected === POINT ? membershipPoint : membershipCashback }}
              </div>
              <!-- The one screen that adds anything to the standard card. -->
              <div class="sheet-balance__meta" v-if="typeSelected === WALLET">
                {{ $t('transactions.pending') }} <span>{{ totalPendingEarnedAmount }}</span>
              </div>
              <div
                class="sheet-balance__meta"
                v-if="merchantMember.allow_show_redeemed_cashback != 0"
              >
                {{ $t('transactions.redeemed') }} <span>{{ totalCashbackUserd }}</span>
              </div>
            </div>
            <span class="material-icons-round sheet-balance__icon">
              {{ typeSelected === POINT ? "stars" : "account_balance_wallet" }}
            </span>
          </div>

          <div
            class="tx-note"
            v-if="isBalanceTab && merchantMember.allow_show_lifetime_earn_cashback != 0"
          >
            <span class="material-icons-outlined">trending_up</span>
            <span>{{ $t('transactions.lifetimeEarning') }} <b>{{ totalLifetimeEarning }}</b></span>
          </div>

          <div class="tx-note" v-if="isBalanceTab && expiryNotices.length">
            <span class="material-icons-outlined">schedule</span>
            <div>
              <div v-for="(text, index) in expiryNotices" :key="index" v-html="text"></div>
            </div>
          </div>

          <!-- Receipt: total spending + tier progress -->
          <div
            class="tx-panel"
            v-if="typeSelected === RECEIPT && showSpendingPanel"
          >
            <div class="tx-panel__row">
              <span class="tx-panel__label">{{ $t('transactions.totalSpending') }}</span>
              <span class="tx-panel__value">
                {{ merchantMember.amount_spent }}
                <em v-if="merchantMember.current_tier_name">{{ merchantMember.current_tier_name }}</em>
              </span>
            </div>
            <div class="tx-progress">
              <div class="tx-progress__track">
                <div
                  class="tx-progress__fill"
                  :style="{ width: (merchantMember.level_percentage || 0) + '%' }"
                ></div>
              </div>
              <img
                v-if="merchantMember.tier_icon"
                :src="merchantMember.tier_icon"
                class="tx-progress__badge"
                alt="tier"
              />
            </div>
            <div class="tx-panel__note" v-if="merchantMember.level_note">
              {{ merchantMember.level_note }}
            </div>
          </div>

          <!-- Donation: total donated -->
          <div class="tx-panel" v-if="typeSelected === DONATION">
            <div class="tx-panel__row">
              <span class="tx-panel__label">{{ $t('transactions.totalDonation') }}</span>
              <span class="tx-panel__value">{{ totalDonation }}</span>
            </div>
            <div class="tx-panel__note">
              {{ $t('transactions.donationThanks') }}
            </div>
          </div>

          <div class="tx-filters">
            <Datepicker
              v-model="timeSelect"
              monthPicker
              auto-apply
              :clearable="false"
              :format="format"
              class="tx-datepicker"
            >
              <template #trigger>
                <button type="button" class="tx-filter">
                  <span class="tx-filter__left">
                    <span class="material-icons-outlined tx-filter__icon">calendar_today</span>
                    {{ monthLabel }}
                  </span>
                  <span class="material-icons-round tx-filter__chevron">expand_more</span>
                </button>
              </template>
            </Datepicker>

            <!-- Hidden only on Donation: that tab reads a different table, whose
                 query has no filter_store clause at all. -->
            <div class="tx-select" v-if="showBranchFilter" ref="branchSelect">
              <button
                type="button"
                class="tx-filter"
                :class="{ 'tx-filter--open': branchSheetOpen }"
                @click="branchSheetOpen = !branchSheetOpen"
              >
                <span class="tx-filter__left">
                  <span class="material-icons-outlined tx-filter__icon">storefront</span>
                  <span class="tx-filter__text">{{ branchLabel }}</span>
                </span>
                <span
                  class="material-icons-round tx-filter__chevron"
                  :class="{ 'tx-filter__chevron--open': branchSheetOpen }"
                  >expand_more</span
                >
              </button>

              <div class="tx-select__menu" v-if="branchSheetOpen">
                <button
                  type="button"
                  class="tx-select__opt"
                  :class="{ 'tx-select__opt--active': !selectedBranchId }"
                  @click="selectBranch(null)"
                >
                  <span class="tx-select__opt-label">{{ $t('transactions.allBranches') }}</span>
                  <span
                    class="material-icons-round tx-select__check"
                    v-if="!selectedBranchId"
                    >check</span
                  >
                </button>
                <button
                  v-for="branch in branches"
                  :key="branch.id"
                  type="button"
                  class="tx-select__opt"
                  :class="{ 'tx-select__opt--active': selectedBranchId === branch.id }"
                  @click="selectBranch(branch)"
                >
                  <span class="tx-select__opt-label">{{ branch.branch_name }}</span>
                  <span
                    class="material-icons-round tx-select__check"
                    v-if="selectedBranchId === branch.id"
                    >check</span
                  >
                </button>
              </div>
            </div>
          </div>

          <template v-for="(group, groupLabel) in visibleGroups" :key="groupLabel">
            <div class="tx-group-label">{{ groupLabel }}</div>
            <div class="tx-list" v-if="group.length">
              <div
                class="tx-row"
                v-for="(value, index) in group"
                :key="groupLabel + '_' + index"
              >
                <div class="tx-row__main">
                  <div class="tx-row__left">
                    <span class="tx-row__icon">
                      <span class="material-icons-outlined">{{ rowIcon(value) }}</span>
                    </span>
                    <div class="tx-row__text">
                      <div class="tx-row__title">{{ value.transaction_label || "" }}</div>
                      <div class="tx-row__date">{{ value.createdAt }}</div>
                      <div class="tx-row__receipt" v-if="value.receipt_no">
                        {{ $t('transactions.receiptNo', { no: value.receipt_no }) }}
                      </div>
                      <!-- Airwallex refunds the store made on this order -->
                      <div
                        v-for="line in refundLines(value)"
                        :key="line.kind"
                        class="tx-row__refund"
                        :class="'tx-row__refund--' + line.kind"
                      >{{ line.text }}</div>
                    </div>
                  </div>
                  <div
                    class="tx-row__amount"
                    :class="
                      numericAmount(value) > 0
                        ? 'tx-row__amount--in'
                        : 'tx-row__amount--out'
                    "
                  >
                    {{ amountText(value) }}
                  </div>
                </div>
                <div
                  class="tx-row__foot"
                  v-if="typeSelected === WALLET && (value.earned_expiry_date || value.earned_pending_time)"
                >
                  <span>
                    {{
                      value.earned_status == 1
                        ? $t('transactions.validUntil', { date: value.earned_expiry_date })
                        : $t('transactions.availableBy', { date: value.earned_pending_time })
                    }}
                  </span>
                  <span
                    class="tx-chip"
                    :class="value.earned_status == 1 ? 'tx-chip--ok' : 'tx-chip--pending'"
                  >
                    {{
                      value.earned_status == 1
                        ? $t('transactions.statusConfirmed')
                        : $t('transactions.statusPending')
                    }}
                  </span>
                </div>
              </div>
            </div>
            <div class="tx-empty" v-else>
              <span class="material-icons-outlined">inbox</span>
              <span>{{ $t('transactions.empty') }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <GuestRewardPrompt
      :show="showGuestPrompt"
      @signin="gotoSignInFromGuestPrompt()"
      @dismiss="goBack()"
    />

    <div class="position-fixed top-1 end-1 z-index-2">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import MooSheetHead from "@/components/MooSheetHead.vue";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import GuestRewardPrompt from "@/components/GuestRewardPrompt.vue";
import MemberService from "../services/member.service";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { localStorageName, gotoMemberLogin } from "@/config/utils";

// Transaction tab ids, same values TransactionController::list switches on.
const WALLET = 1;
const RECEIPT = 2;
const DONATION = 3;
const POINT = 11;
// merchant_setting.reward_per_transaction value that turns the Point tab on.
const REWARD_POINT = 2;

export default {
  name: "MyTransactions",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    GuestRewardPrompt,
    Datepicker,
    MooSheetHead,
  },
  data() {
    return {
      WALLET,
      RECEIPT,
      DONATION,
      POINT,
      isLogined: false,
      isFirstLoadDone: false,
      showGuestPrompt: false,
      branchCode: "",
      tableCode: "",
      pathType: "",
      typeSelected: WALLET,
      listTransaction: [],
      merchantMember: {},
      pointName: "Point",
      membershipCashback: "",
      membershipPoint: "",
      totalPendingEarnedAmount: "",
      totalCashbackUserd: "",
      totalLifetimeEarning: "",
      totalDonation: 0,
      timeSelect: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
      },
      branches: [],
      selectedBranchId: null,
      selectedBranchName: "",
      branchSheetOpen: false,
      loaderActive: false,
      snackbar: null,
      message: "",
      // Sequence token for getMyTransactions(). Tab, month and branch each fire
      // their own request without cancelling the one in flight, so responses can
      // land out of order; only the newest is allowed to write to state.
      requestId: 0,
    };
  },
  created() {
    this.pathType = this.$route.params.type;
    this.branchCode = this.$store.state.branchCode;
    this.tableCode = this.$route.params.table_code;

    const memberInfoData = JSON.parse(
      localStorage.getItem(localStorageName("memberInfo")) || "null"
    );
    this.isLogined = !!memberInfoData;
  },
  async mounted() {
    document.addEventListener("click", this.closeBranchSelectOnOutsideClick);
    if (!this.isLogined) {
      this.isFirstLoadDone = true;
      this.showGuestPrompt = true;
      return;
    }
    this.checkMemberPermissions();
    // Branches first, deliberately sequential: the default filter is the branch
    // being ordered from, and it has to be known before the first list call —
    // otherwise every branch's history paints and then narrows a moment later.
    await this.getBranches();
    await this.getMyTransactions();
    this.isFirstLoadDone = true;
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeBranchSelectOnOutsideClick);
  },
  computed: {
    styleObject() {
      // Same base the rest of the reskinned MOO screens use (TopUp.vue,
      // MyReward.vue) so all --tx-* tints track the merchant palette.
      return {
        "--color": this.$store.state.mainThemeColorTheme2
          ? this.$store.state.mainThemeColorTheme2
          : this.$store.state.color,
      };
    },
    tabs() {
      const tabs = [{ type: WALLET, label: this.$t("transactions.tabWallet") }];
      if (this.merchantMember.reward_type == REWARD_POINT) {
        tabs.push({ type: POINT, label: this.pointName });
      }
      tabs.push({ type: RECEIPT, label: this.$t("transactions.tabReceipt") });
      if (this.merchantMember.show_donation != 0) {
        tabs.push({ type: DONATION, label: this.$t("transactions.tabDonation") });
      }
      return tabs;
    },
    isBalanceTab() {
      return [WALLET, POINT].includes(this.typeSelected);
    },
    showBranchFilter() {
      return this.typeSelected !== DONATION;
    },
    showSpendingPanel() {
      return (
        this.merchantMember.allow_setup_membership_tiering != 0 &&
        this.merchantMember.allow_show_membership_spending != 0
      );
    },
    // Rows worth nothing are dropped before render. The clearest case is the
    // "Topup bonus" row the API synthesises for every top-up: on a package with
    // no bonus it arrives as + $ 0.00 and tells the member nothing. The rule is
    // applied to every row, not just that one — a zero movement is noise on any
    // tab.
    visibleGroups() {
      const groups = {};

      Object.keys(this.listTransaction ?? {}).forEach((label) => {
        groups[label] = (this.listTransaction[label] ?? []).filter(
          (value) => this.numericAmount(value) !== 0
        );
      });

      return groups;
    },
    // Each balance tab has its own expiry list: cashback on Wallet, points on
    // Point. Reading only the cashback one meant the Point tab silently showed
    // nothing even once the API started returning point notices.
    expiryNotices() {
      return this.typeSelected === POINT
        ? this.merchantMember.notice_transaction_point_expiry ?? []
        : this.merchantMember.notice_transaction_amount_expiry ?? [];
    },
    // Empty means no branch filter, which reads as "all branches" — kept as a
    // label rather than a stored string so it follows a language switch.
    branchLabel() {
      return this.selectedBranchName || this.$t("transactions.allBranches");
    },
    monthLabel() {
      return this.format(
        new Date(this.timeSelect.year, this.timeSelect.month, 1)
      );
    },
  },
  methods: {
    // Profile only hides its Transactions row when the merchant withholds the
    // permission — without this, the screen is still reachable by URL. Same
    // guard TopUp.vue applies for "Top-up".
    checkMemberPermissions() {
      MemberService.checkMemberPermissions().then(
        (response) => {
          if (
            response.data.result.isSuccess &&
            !response.data.data.includes("Transactions")
          ) {
            this.goBack();
          }
        },
        () => {}
      );
    },
    format(date) {
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${month}/${date.getFullYear()}`;
    },
    goBack() {
      // Same reasoning as TopUp.vue's goBack(): push Profile directly rather
      // than router.go(-1), which can land outside the app.
      this.$router.push({
        name: "Profile",
        params: {
          merchant_code: this.$store.state.merchantCode,
          type: this.pathType,
          branch_code: this.branchCode,
          table_code: this.tableCode,
        },
      });
    },
    gotoSignInFromGuestPrompt() {
      this.showGuestPrompt = false;
      gotoMemberLogin("my-transactions", false, "login");
    },
    changeType(type) {
      if (this.typeSelected === type) {
        return;
      }
      this.typeSelected = type;
      this.branchSheetOpen = false;

      // The Wallet response carries BOTH expiry lists (see
      // TransactionController::list), so switching to Point renders the point
      // notice out of the response we are about to replace — it appears, then
      // vanishes when the real Point response lands. Blank both until it does:
      // showing nothing for a moment beats showing something from the tab the
      // member just left.
      this.merchantMember = {
        ...this.merchantMember,
        notice_transaction_amount_expiry: [],
        notice_transaction_point_expiry: [],
      };

      this.getMyTransactions();
    },
    // Dismiss like a native select: any click that lands outside the trigger
    // and its menu closes it. The toggle button itself sits inside the ref, so
    // this listener never fights with its own @click.
    closeBranchSelectOnOutsideClick(event) {
      if (!this.branchSheetOpen) {
        return;
      }
      const root = this.$refs.branchSelect;
      if (root && !root.contains(event.target)) {
        this.branchSheetOpen = false;
      }
    },
    selectBranch(branch) {
      this.selectedBranchId = branch ? branch.id : null;
      this.selectedBranchName = branch ? branch.branch_name : "";
      this.branchSheetOpen = false;
      this.getMyTransactions();
    },
    isCredit(value) {
      return (value.transaction_type_object?.SIGN ?? "+") === "+";
    },
    // Which of the two figures a row reports, as the API formatted it. Wallet
    // "Spent at" rows report what came out of the wallet (amount_direct_deduct),
    // not the receipt total — same split the member site makes.
    //
    // One definition on purpose: amountText() prints it and numericAmount()
    // reads its sign, and the two picking different fields would colour a row
    // against the number printed on it.
    rowAmountText(value) {
      return this.typeSelected === WALLET && value.transaction_type == 2
        ? value.amount_direct_deduct_format
        : value.payment_amount_format;
    },
    // airwallex_refund from my-transactions: refunded / processing / the latest one failed.
    refundLines(value) {
      const refund = value.airwallex_refund;
      if (!refund) return [];
      const money = (amount) => "$ " + Number(amount).toFixed(2);
      const lines = [];
      if (refund.refunded > 0) lines.push({ kind: "done", text: this.$t("transactions.refunded", { amount: money(refund.refunded) }) });
      if (refund.processing > 0) lines.push({ kind: "processing", text: this.$t("transactions.refundProcessing", { amount: money(refund.processing) }) });
      if (refund.failed) lines.push({ kind: "failed", text: this.$t("transactions.refundFailed") });
      return lines;
    },
    amountText(value) {
      return (
        (value.transaction_type_object?.SIGN ?? "") +
        (this.rowAmountText(value) ?? "")
      );
    },
    // The signed value of a row, for colouring and for the zero check.
    //
    // Parsed from the formatted string rather than a raw column: the synthesised
    // "Topup bonus" row carries no numeric field at all, only the formatted
    // bonus. Matching the FIRST number in the string, instead of stripping every
    // non-digit, keeps formatPoint()'s "120 Points" from reading as 120 plus
    // whatever digits a merchant put in their point name.
    numericAmount(value) {
      const match = String(this.rowAmountText(value) ?? "").match(/[\d,]+(\.\d+)?/);

      if (!match) {
        return 0;
      }

      const amount = parseFloat(match[0].replace(/,/g, ""));

      if (isNaN(amount)) {
        return 0;
      }

      return this.isCredit(value) ? amount : -amount;
    },
    rowIcon(value) {
      const label = (value.transaction_label || "").toLowerCase();
      // The "Topup bonus" row is synthesised by the API without a
      // transaction_type, so its label is the only thing to key off.
      if (label.includes("bonus")) {
        return "redeem";
      }
      switch (value.transaction_type) {
        case 6:
          return "north_east";
        case 2:
        case 12:
          return "shopping_bag";
        case 3:
          return "confirmation_number";
        case 4:
          return "group_add";
        case 5:
          return "tune";
        case 10:
          return "workspace_premium";
        default:
          return this.typeSelected === DONATION
            ? "volunteer_activism"
            : "savings";
      }
    },
    async getMyTransactions() {
      this.loaderActive = true;
      this.listTransaction = [];
      const params = this.showBranchFilter
        ? { filter_store: this.selectedBranchId ? [this.selectedBranchId] : [] }
        : {};

      // Claimed before the call goes out; anything that resolves after a newer
      // request started is a stale answer for a tab the member has already left.
      const ticket = ++this.requestId;
      const isStale = () => ticket !== this.requestId;

      await MemberService.getMyTransactions(
        this.typeSelected,
        this.timeSelect,
        params
      ).then(
        (response) => {
          if (isStale()) {
            return;
          }
          if (response.data.result.isSuccess) {
            const data = response.data.data;
            this.listTransaction = data.list_transaction ?? [];
            this.merchantMember = data.merchant_member ?? {};
            this.pointName = data.point_name || "Point";
            this.membershipCashback = data.membership_cashback || "S$ 0";
            this.membershipPoint = data.membership_point || "S$ 0";
            this.totalPendingEarnedAmount = data.total_pending_earned_amount || "S$ 0";
            this.totalCashbackUserd = data.total_cashback_userd || "S$ 0";
            this.totalLifetimeEarning = data.total_lifetime_earning || "S$ 0";
            this.totalDonation = data.total_donation || "S$ 0";
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.loaderActive = false;
        },
        (error) => {
          if (isStale()) {
            return;
          }
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? this.$t("transactions.loadError");
          this.setTimeOutNotification();
          this.loaderActive = false;
        }
      );
    },
    async getBranches() {
      await MemberService.getBranches().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.branches = response.data.data ?? [];
            this.applyDefaultBranch();
          }
        },
        () => {}
      );
    },
    // Open on the branch the member is ordering from rather than on every
    // branch: they came here from that outlet, so its history is what they mean.
    //
    // Matched on branch_code — the only key the route and this list are certain
    // to share. BRANCH_INFO in localStorage might carry an id, but nothing else
    // in the app reads one off it, so it is not something to depend on here.
    applyDefaultBranch() {
      if (!this.branchCode) {
        return;
      }

      const code = String(this.branchCode).toLowerCase();
      const current = this.branches.find(
        (branch) => String(branch.branch_code ?? "").toLowerCase() === code
      );

      // Ordering from a branch the member endpoint does not list: leave the
      // filter on "All branches" rather than selecting nothing.
      if (!current) {
        return;
      }

      this.selectedBranchId = current.id;
      this.selectedBranchName = current.branch_name;
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
  watch: {
    timeSelect(value) {
      if (value) {
        this.getMyTransactions();
      }
    },
  },
};
</script>

<style scoped>
/* Base MOO page shell — same contract TopUp.vue / MyReward.vue rely on:
   container-custom fills the viewport height, content-custom is the scroll
   region. */
.container-custom {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.content-custom {
  flex: 1;
  overflow: auto;
}

/* Tokens derived from the merchant brand colour, same color-mix recipe as
   TopUp.vue's --tp-* so the two screens read as one system. */
.moo-transactions-page {
  /* Aliases onto the shared set in assets/css/moo-sheet.css so the
     colour recipe lives in exactly one place. */
  --tx: var(--sheet);
  --tx-08: var(--sheet-08);
  --tx-12: var(--sheet-12);
  --tx-border: var(--sheet-border);
  --tx-inset: var(--sheet-inset);
}



.tx-tabs {
  display: flex;
  background: #ffffff;
  border: 1px solid var(--tx-border);
  border-radius: 12px;
  padding: 3px;
  margin-bottom: 16px;
}
.tx-tab {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  border-radius: 9px;
  padding: 8px 4px;
  font-size: 13px;
  font-weight: 600;
  color: #857372;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
}
.tx-tab--active {
  background: var(--tx);
  color: #fff;
}

/* Spacing only; the card itself comes from moo-sheet.css. */
.tx-balance {
  margin-bottom: 12px;
}

.tx-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: #6b5d55;
  background: var(--tx-08);
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 12px;
}
.tx-note .material-icons-outlined {
  font-size: 16px;
  color: var(--tx);
  flex-shrink: 0;
}
.tx-note b {
  color: #241a12;
}

.tx-panel {
  background: #ffffff;
  border: 1px solid var(--tx-border);
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.tx-panel__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.tx-panel__label {
  font-size: 13px;
  color: #857372;
}
.tx-panel__value {
  font-size: 18px;
  font-weight: 800;
  color: var(--tx);
}
.tx-panel__value em {
  font-style: normal;
  font-size: 12px;
  font-weight: 600;
  color: #857372;
  margin-left: 6px;
}
.tx-panel__note {
  font-size: 12px;
  color: #857372;
  margin-top: 8px;
}
.tx-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.tx-progress__track {
  flex: 1;
  height: 6px;
  border-radius: 9999px;
  background: var(--tx-12);
  overflow: hidden;
}
.tx-progress__fill {
  height: 100%;
  border-radius: 9999px;
  background: var(--tx);
}
.tx-progress__badge {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.tx-filters {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
.tx-datepicker {
  flex: 1;
  min-width: 0;
}
.tx-filter {
  width: 100%;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  background: #ffffff;
  border: 1px solid var(--tx-border);
  border-radius: 12px;
  padding: 9px 12px;
  font-size: 13px;
  color: #241a12;
  cursor: pointer;
}
.tx-filter__left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
/* See RewardCatalogue: ellipsis needs its own element, not the flex parent. */
.tx-filter__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* No optical nudge here on purpose: measured in Chrome at 1x, the icon ink
   centre already lands within half a pixel of the text ink centre, and every
   1px offset tried made it visibly worse. 16px (not 15) keeps the strokes on
   the icon font's own even pixel grid rather than straddling half-pixels. */
.tx-filter__icon {
  font-size: 16px;
  color: var(--tx);
  flex-shrink: 0;
}
.tx-filter__chevron {
  font-size: 18px;
  color: #857372;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}

/* Branch filter behaves like a native select: the menu floats over the list
   instead of pushing it down, and scrolls internally once a merchant has more
   branches than fit. */
.tx-select {
  position: relative;
  flex: 1;
  min-width: 0;
}
.tx-filter--open {
  border-color: var(--tx);
}
.tx-filter__chevron--open {
  transform: rotate(180deg);
}
.tx-select__menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  background: #ffffff;
  border: 1px solid var(--tx-border);
  border-radius: 12px;
  padding: 6px;
  max-height: 260px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 12px 28px rgba(25, 28, 30, 0.16);
}
.tx-select__opt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 9px 10px;
  font-size: 13px;
  border-radius: 8px;
  color: #241a12;
  cursor: pointer;
}
.tx-select__opt-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tx-select__opt--active {
  background: var(--tx-08);
  color: var(--tx);
  font-weight: 700;
}
.tx-select__check {
  font-size: 16px;
  color: var(--tx);
  flex-shrink: 0;
}

.tx-group-label {
  font-size: 13px;
  color: #857372;
  margin: 0 0 10px;
}

.tx-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}
.tx-row {
  background: #ffffff;
  border: 1px solid var(--tx-border);
  border-radius: 14px;
  padding: 11px 14px;
}
.tx-row__main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.tx-row__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.tx-row__icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--tx-08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tx-row__icon .material-icons-outlined {
  font-size: 16px;
  color: var(--tx);
}
.tx-row__text {
  min-width: 0;
}
.tx-row__title {
  font-size: 14px;
  font-weight: 700;
  color: #241a12;
  word-break: break-word;
}
.tx-row__date {
  font-size: 12px;
  color: #857372;
  margin-top: 2px;
}
.tx-row__refund {
  font-size: 11px;
  font-weight: 600;
  margin-top: 2px;
}
.tx-row__refund--done {
  color: #2e7d32;
}
.tx-row__refund--processing {
  color: #8d6e00;
}
.tx-row__refund--failed {
  color: #c62828;
}
.tx-row__receipt {
  font-size: 11px;
  color: #857372;
  margin-top: 2px;
}
.tx-row__amount {
  font-size: 14px;
  font-weight: 800;
  flex-shrink: 0;
  white-space: nowrap;
}
/* Semantic, not the merchant accent: money in and money out have to stay
   readable as in/out on a merchant whose brand colour happens to be red or
   green. The red matches .sheet-status--expired in moo-sheet.css. */
.tx-row__amount--in {
  color: #1f7a4d;
}
.tx-row__amount--out {
  color: #a5231b;
}
.tx-row__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--tx-border);
  font-size: 12px;
  color: #6b5d55;
}
.tx-chip {
  font-size: 10.5px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 8px;
  flex-shrink: 0;
}
.tx-chip--ok {
  color: #30781c;
  background: rgba(48, 120, 28, 0.1);
}
.tx-chip--pending {
  color: #25507a;
  background: rgba(37, 80, 122, 0.1);
}

.tx-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 28px 0;
  color: #857372;
  font-size: 13px;
}
.tx-empty .material-icons-outlined {
  font-size: 28px;
  color: var(--tx-border);
}
</style>

<style>
/* Unscoped: the datepicker renders its menu in its own subtree, so the
   trigger button has to lose the library's default input chrome globally. */
.moo-transactions-page .tx-datepicker .dp__input_wrap {
  display: block;
}
.moo-transactions-page .tx-datepicker .dp__theme_light {
  --dp-primary-color: var(--color);
}
</style>
